import type { HistoryItem, LineItem, ProductListV2 } from '../ShoppingCart.types';
import { CartCheckoutSteps, CartSections, ShippingAddressOptions, ShippingMethods } from '../ShoppingCart.types';
import type { State, DerivedState, HistoryState } from './ShoppingCart.provider';
import type { GetEventRegistriesAndOrdersQuery } from '../../../../guest/routes/GuestRegistry/components/GuestShopping/GuestShopping.graphql.generated';
import type { RegistryOrderFragment } from '@graphql/fragments/registry.graphql.generated';
import { getLocalStorage } from '@shared/core';
import { clamp } from 'lodash-es';

const localStorage = getLocalStorage();

export const NATIONAL_COUNTRY_CODE = 'USA';
export const NATIONAL_COUNTRY = 'United States';

export const STATE_INITIAL: State = {
  itemList: [],
  lastUpdated: null,
  hadOnlyStillNeededItems: true,
};

export const HISTORY_STATE_INITIAL: HistoryState = {
  historyItemList: [],
};

export const DERIVED_STATE_INITIAL: DerivedState = {
  itemListCount: 0,
  cartItemCount: 0,

  // Section & Step
  checkoutStepsFlow: [],
  currentSection: CartSections.CART,
  currentCheckoutStep: CartCheckoutSteps.Cart,

  // Shipping Step
  shippingMethod: ShippingMethods.FreeByJoy,
  selectedShippingOption: ShippingAddressOptions.CoupleAddress,
  supportsShipToMe: false,

  // Details Step
  details: { message: '', name: '', email: '' },

  // General Cart
  isShoppingCartOpen: false,
  shippingFee: undefined,
  shippingFeeInMajorUnits: 0,
  surchargeFee: undefined,
  additionalCostLines: [],
  additionalCostInMajorUnits: 0,
  taxesTotalInMajorUnits: undefined,
  isSyncing: false,
  lastSyncAt: null,
};

export const getInitialState = (shoppingCartKey: string) => {
  const storageDataString = localStorage.getItem(shoppingCartKey);

  if (!storageDataString) {
    return STATE_INITIAL;
  }

  try {
    const storageData = JSON.parse(storageDataString);
    return storageData;
  } catch (err) {
    return STATE_INITIAL;
  }
};

export const getInitialHistoryState = (shoppingCartHistoryKey: string) => {
  const storageDataString = localStorage.getItem(shoppingCartHistoryKey);

  if (!storageDataString) {
    return HISTORY_STATE_INITIAL;
  }

  try {
    const storageData = JSON.parse(storageDataString);
    return storageData;
  } catch (err) {
    return HISTORY_STATE_INITIAL;
  }
};

export type ProductStillNeeded = { stillNeeded: number; id: string };

export const getProductsStillNeeded = (data: GetEventRegistriesAndOrdersQuery): Maybe<Array<ProductStillNeeded>> => {
  const registry = data.eventByName?.info.registry;

  if (!registry) {
    return null;
  }

  // Get all registry products into a flat list
  return registry.reduce((acc: Array<ProductStillNeeded>, registryEntry) => {
    if (registryEntry?.items) return [...acc, ...registryEntry.items.map(item => ({ stillNeeded: item.stillNeeded, id: item.id }))];
    else return acc;
  }, []);
};

export const isLineItemStillNeeded = (item: LineItem, productsStillNeeded: Array<ProductStillNeeded>) =>
  productsStillNeeded.some(product => product.stillNeeded > 0 && product.stillNeeded >= item.quantity && product.id === item.registryItemId);

export const isLineItemKnownUnavailable = (item: LineItem, productsStillNeeded: Array<ProductStillNeeded>) => {
  const product = productsStillNeeded.find(candidate => candidate.id === item.registryItemId);
  return !!product && product.stillNeeded < item.quantity;
};

export const getStillNeeded = (prevState: State, data: GetEventRegistriesAndOrdersQuery) => {
  const productsStillNeeded = getProductsStillNeeded(data);

  if (productsStillNeeded) {
    // Check every item from state to be in the product list
    const newState = {
      ...prevState,
      itemList: prevState.itemList.filter(item => isLineItemStillNeeded(item, productsStillNeeded)),
    };

    newState.hadOnlyStillNeededItems = newState.itemList.length === prevState.itemList.length;

    return newState;
  } else {
    return prevState;
  }
};

export const getUpdatedStateByRemoveItem = (prevState: State, registryItemId: string) => {
  const itemList = prevState.itemList.filter(item => registryItemId !== item.registryItemId);

  const newState = {
    ...prevState,
    itemList,
    lastUpdated: Date.now(),
  };

  return newState;
};

export const getUpdatedStateByAddItem = (prevState: State, data: LineItem, maxQuantity: number) => {
  let newState;
  const index = prevState.itemList.findIndex(item => data.registryItemId === item.registryItemId);

  if (index === -1) {
    newState = {
      ...prevState,
      itemList: [...prevState.itemList, data],
      lastUpdated: Date.now(),
    };
  } else {
    const item = prevState.itemList[index];
    const quantity = Math.min(item.quantity + data.quantity, maxQuantity);

    newState = getUpdatedStateByUpdateItem(prevState, { ...item, quantity });
  }

  return newState;
};

export const getBuyNowLineItem = (data: LineItem, maxQuantity: number): LineItem => ({
  ...data,
  quantity: clamp(data.quantity, 1, maxQuantity),
});

export const getUpdatedStateByUpdateItem = (prevState: State, data: LineItem) => {
  const index = prevState.itemList.findIndex(item => data.registryItemId === item.registryItemId);

  if (index === -1) {
    return prevState;
  }

  const itemList = [...prevState.itemList];
  itemList[index] = data;
  const newState = {
    ...prevState,
    itemList,
    lastUpdated: Date.now(),
  };

  return newState;
};

export const getMarkedAsVisited = (prevState: HistoryState, reservedOrderList: RegistryOrderFragment[], productList: ProductListV2) => {
  const historyItemList = reservedOrderList.reduce((acc, order) => {
    const registryItemId = order?.lineItems?.filter(lineItem => lineItem.registryItem)[0].registryItem?.id;
    const product = productList.products.find(product => product.id === registryItemId);
    if (product) {
      const visited = prevState.historyItemList.find(item => item.registryItemId === product.registryItemId && order.id === item.orderId);
      if (!visited) return [...acc, { registryItemId: product.registryItemId, orderId: order.id }];
    }

    return acc;
  }, prevState.historyItemList as Array<HistoryItem>);

  return { ...prevState, historyItemList };
};
